/**
 * Starting a Record & Review attempt.
 *
 * Found on staging: Start Training → POST /practice/sessions answered 403
 * PLAN_LIMIT, yet the Practice page recorded anyway and uploaded the audio to
 * POST /voice/practice/analyze — provider work after a refusal. The rule here:
 * an attempt that belongs to a practice session (training, NPC, AI coach)
 * records ONLY after that session was created. Any refusal (PLAN_LIMIT,
 * ORG_PLAN_LIMIT, validation, not found, network) starts nothing — no
 * microphone, no recording, so nothing can be uploaded or analyzed.
 *
 * Free practice has no session to create (`plan` is null) and records directly;
 * its analysis is metered by the voice-analysis quota on the server.
 */

export interface RecordedAttemptPlan<T> {
  /** Create the practice session. Rejects when the server refuses the start. */
  create: () => Promise<T>;
  /** The start was refused: show why. Nothing has been started. */
  onRefused: (error: unknown) => void;
}

export interface RecordedAttemptHooks<T> {
  onCreated: (created: T) => void;
  startRecording: () => Promise<void>;
}

/** Returns true when recording started, false when the start was refused. */
export async function beginRecordedAttempt<T>(
  plan: RecordedAttemptPlan<T> | null,
  { onCreated, startRecording }: RecordedAttemptHooks<T>
): Promise<boolean> {
  if (plan) {
    let created: T;
    try {
      created = await plan.create();
    } catch (error) {
      plan.onRefused(error);
      return false;
    }
    onCreated(created);
  }
  await startRecording();
  return true;
}
